/**
 * Skills screen view-model (pure, DOM-free, unit-tested). Rotation tab: the main hero's bound
 * abilities (with the active talents applied), each ability's header facts and description from
 * the shared tooltip writer (hud/tooltip), and which talents touch it. Talents tab: the node list
 * grouped by tier with one short lock line per row, and the ring picks (capstone in the centre).
 */
import type { AbilityDef, ClassDef, HotbarKey } from '@keystone/sim';
import { HOTBAR_KEYS } from '@keystone/sim';
import type { ClassTransform, TalentId } from '@keystone/progression';
import { applyTransforms, findAura, isCapstone, talentNode, talentTree } from '@keystone/progression';
import { describeAbility } from '../../hud/tooltip/describe';
import type { TooltipContent } from '../../hud/tooltip/types';
import { describeNode, lockReasonText, type NodeView, type TalentGridView } from './talentsModel';

// ---------------------------------------------------------------------------
// Class with talents applied
// ---------------------------------------------------------------------------

/** Apply each node's transforms in order, skipping any that cannot apply (never throws). */
export function applyNodes(def: ClassDef, nodes: readonly TalentId[]): ClassDef {
  let cur = def;
  for (const id of nodes) {
    const n = talentNode(id);
    if (!n || n.classId !== def.id) continue;
    for (const t of n.effect) {
      try {
        cur = applyTransforms(cur, [t as ClassTransform]);
      } catch {
        /* not applicable */
      }
    }
  }
  return cur;
}

/** Tooltip content for a compiled class (its abilities / inline auras first, then the registry). */
export function compiledContent(def: ClassDef, fallback?: TooltipContent): TooltipContent {
  return {
    ability: (id) => def.abilities.find((a) => a.id === id) ?? fallback?.ability(id),
    aura: (id) => findAura(def, id) ?? fallback?.aura(id),
  };
}

// ---------------------------------------------------------------------------
// Rotation rows
// ---------------------------------------------------------------------------

export interface RotationRow {
  abilityId: string;
  name: string;
  /** Hotbar key ("1", "Q"), null when unbound. */
  key: HotbarKey | null;
  /** Set on a locked row: the talent that grants it. */
  lockedBy?: { nodeId: TalentId; name: string };
}

const EXTRA_KEYS = ['E', 'F', 'G'] as const;

/** Bound abilities in hotbar order (talented class), then abilities granted by unlearned talents (locked). */
export function rotationRows(base: ClassDef, nodes: readonly TalentId[]): RotationRow[] {
  const def = applyNodes(base, nodes);
  const rows: RotationRow[] = [];
  const seen = new Set<string>();
  const binds = { ...(def.binds as Record<string, string>), ...((def.extraBinds ?? {}) as Record<string, string>) };
  const order = [...HOTBAR_KEYS, ...EXTRA_KEYS.filter((k) => !HOTBAR_KEYS.includes(k as HotbarKey))];
  for (const k of order) {
    const id = binds[k];
    if (!id || seen.has(id)) continue;
    const a = def.abilities.find((x) => x.id === id);
    if (!a) continue;
    seen.add(id);
    rows.push({ abilityId: id, name: a.name, key: k as HotbarKey });
  }
  for (const n of talentTree(base.id)) {
    if (nodes.includes(n.id)) continue;
    for (const t of n.effect) {
      if (t.op !== 'addAbility' || seen.has(t.ability.id)) continue;
      seen.add(t.ability.id);
      rows.push({ abilityId: t.ability.id, name: t.ability.name, key: null, lockedBy: { nodeId: n.id, name: n.name } });
    }
  }
  return rows;
}

/** The ability definition for a row (talented class, or the granting node's ability for a locked row). */
export function rowAbility(base: ClassDef, nodes: readonly TalentId[], row: RotationRow): AbilityDef | null {
  if (row.lockedBy) return applyNodes(base, [...nodes, row.lockedBy.nodeId]).abilities.find((a) => a.id === row.abilityId) ?? null;
  return applyNodes(base, nodes).abilities.find((a) => a.id === row.abilityId) ?? null;
}

export interface AbilityInfo {
  name: string;
  /** "Instant · 6 sec cooldown" (cast + cooldown + charges). */
  facts: string;
  /** "30 Rage" / "Generates 20 Rage" (shown in blue). */
  cost: string;
  /** Description paragraph(s). */
  desc: string[];
  /** Other noteworthy lines (procs, requirements), at most 3. */
  extra: string[];
}

/** Header facts and text of an ability, from the shared tooltip writer. */
export function abilityInfo(a: AbilityDef, def: ClassDef, fallback?: TooltipContent): AbilityInfo {
  try {
    const m = describeAbility(a, null, compiledContent(def, fallback), { classDef: def, baseAbility: null, iconUrl: null });
    const facts = [m.cast, m.cooldown, m.charges].filter((x): x is string => !!x).join(' · ');
    const desc = m.lines.filter((l) => l.tone === 'desc').map((l) => l.text);
    const extra = m.lines.filter((l) => l.tone === 'talent' || l.tone === 'good').map((l) => l.text).slice(0, 3);
    return { name: m.title || a.name, facts, cost: m.cost ?? '', desc, extra };
  } catch {
    return { name: a.name, facts: '', cost: '', desc: [], extra: [] };
  }
}

// ---------------------------------------------------------------------------
// Which talents touch an ability
// ---------------------------------------------------------------------------

export interface AbilityTalent {
  nodeId: TalentId;
  name: string;
  /** What the node does to this ability (first sentence). */
  change: string;
}

const touchCache = new WeakMap<ClassDef, Map<string, AbilityTalent[]>>();

/** abilityId → talents that change it (from describeNode, grouped by button). Cached per class def. */
export function talentsByAbility(base: ClassDef): Map<string, AbilityTalent[]> {
  const hit = touchCache.get(base);
  if (hit) return hit;
  const out = new Map<string, AbilityTalent[]>();
  const nameToId = new Map<string, string>();
  for (const a of base.abilities) nameToId.set(a.name, a.id);
  for (const n of talentTree(base.id)) {
    for (const t of n.effect) if (t.op === 'addAbility') nameToId.set(t.ability.name, t.ability.id);
  }
  for (const n of talentTree(base.id)) {
    let groups: ReturnType<typeof describeNode> = [];
    try {
      groups = describeNode(n, base);
    } catch {
      groups = [];
    }
    for (const g of groups) {
      const id = g.ability ? nameToId.get(g.ability) : undefined;
      if (!id) continue;
      const list = out.get(id) ?? [];
      if (!list.some((x) => x.nodeId === n.id)) list.push({ nodeId: n.id, name: n.name, change: g.changes[0] ?? n.description });
      out.set(id, list);
    }
  }
  touchCache.set(base, out);
  return out;
}

// ---------------------------------------------------------------------------
// Talent list
// ---------------------------------------------------------------------------

export interface TalentRow {
  view: NodeView;
  /** "1 pt" / "2 pts". */
  costText: string;
  /** One short lock line for a locked / excluded row ('' otherwise). */
  lockText: string;
  capstone: boolean;
}

export interface TalentTierGroup {
  tier: 1 | 2 | 3;
  label: string;
  /** "Unlocks at 5 points" / "Reach Champion" ('' when open). */
  lockText: string;
  rows: TalentRow[];
}

const TIER_LABEL: Readonly<Record<number, string>> = { 1: 'Tier I', 2: 'Tier II', 3: 'Tier III' };

/** Short lock line: the first reason, in a few words. */
export function shortLock(v: NodeView): string {
  const r = v.reasons[0];
  if (!r) return '';
  switch (r.kind) {
    case 'tier':
      return `Unlocks at ${r.needed} points`;
    case 'capstone':
      return `Unlocks at ${r.needed} points`;
    case 'rank':
      return `${r.rankName} rank`;
    case 'prereq':
      return `Needs ${r.name}`;
    case 'points':
      return `Needs ${r.cost} pt${r.cost === 1 ? '' : 's'}`;
    case 'choice':
      return `Swaps ${r.takenName}`;
    default:
      return lockReasonText(r);
  }
}

export function talentGroups(grid: TalentGridView): TalentTierGroup[] {
  return grid.gates.map((g) => ({
    tier: g.tier,
    label: TIER_LABEL[g.tier] ?? `Tier ${g.tier}`,
    lockText: g.rankLocked ? `${g.rankName} rank` : g.met ? '' : `Unlocks at ${g.needed} points`,
    rows: grid.nodes
      .filter((v) => v.node.tier === g.tier)
      .sort((a, b) => a.node.col - b.node.col)
      .map((v) => ({
        view: v,
        costText: `${v.node.cost} pt${v.node.cost === 1 ? '' : 's'}`,
        lockText: v.state === 'locked' || v.state === 'excluded' ? shortLock(v) : '',
        capstone: isCapstone(v.node),
      })),
  }));
}

/** Ring picks: the learned capstone (or the highest-tier pick) in the centre, up to 6 other picks around. */
export function ringPicks(nodes: readonly TalentId[]): { center: TalentId | null; around: TalentId[] } {
  const known = nodes.map((id) => talentNode(id)).filter((n): n is NonNullable<typeof n> => !!n);
  const sorted = [...known].sort((a, b) => b.tier - a.tier || b.cost - a.cost);
  const cap = sorted.find((n) => isCapstone(n)) ?? null;
  const center = cap ?? sorted[0] ?? null;
  const around = sorted.filter((n) => n !== center).slice(0, 6).map((n) => n.id);
  return { center: center?.id ?? null, around };
}

/** Points line for the header: remaining and total. */
export function pointsLine(grid: Pick<TalentGridView, 'remaining' | 'points'>): { remaining: number; total: number } {
  return { remaining: Math.max(0, grid.remaining), total: grid.points };
}
